import { ILocalVideoTrack, IRemoteVideoTrack } from "agora-rtc-sdk-ng";
import { UserColor, UserColorOld } from "./css/color";

// Space
export type Space = {
  id: string;
};

export type SpaceInfo = {
  owners?: string[]; // userIDs
  name?: string;
  spatialAudio: boolean;
  cursorSize: number;
  archived?: boolean;
  starterShown?: boolean;
};

export type FeatureFlags = {
  webBlock: boolean;
  edgeScroll: boolean;
  agoraStat: boolean;
  shouldShowFpsMeter: boolean;
};

export const defaultFeatureFlags: FeatureFlags = {
  webBlock: true,
  edgeScroll: false,
  agoraStat: false,
  shouldShowFpsMeter: false,
};

export const featureFlagLabels = {
  webBlock: "Interactive Web Block",
  edgeScroll: "Nudge Scroll",
  shouldShowFpsMeter: "Show FPS Meter",
  agoraStat: "Show Agora Stat",
};

// Blocks
export type Block = {
  id: string;
  position: Position; // Vector2D
  size: Size; // Vector2D
  updatedAt: number; // stored as timestamp
  deleted?: boolean;
  lockedBy?: User | null; // when editing not sure if needed
  madeBy?: string | null; // User Id that created a block
  value?: string | null;
  type: BlockType;
  disabled?: boolean; // turn CSS block on and off
  locked?: boolean; // locked to canvas
};

export type AnyBlock =
  | JSONTextBlock
  | TextBlock
  | FaceBlock
  | WebBlock
  | ImageBlock
  | ImageSearchBlock
  | LinkBlock
  | CssBlock
  | ScreenBlock
  | SvgBlock
  | YoutubeBlock
  | MenuBlock;

export type BlockType =
  | "text/json"
  | "text"
  | "face"
  | "web"
  | "image"
  | "imageSearch"
  | "link"
  | "css"
  | "svg"
  | "screen"
  | "youtube"
  | "draw"
  | "menu";

export type MenuBlock = Block & {
  type: "menu";
  scroll?: {
    value: number;
    userId: string;
  };
};

export type TextBlock = Block & {
  type: "text";
};

export type JSONTextBlock = Block & {
  type: "text/json";
  // Backup plaintext from migration
  backupText?: string;
};

export type FaceBlock = Block & {
  type: "face";
};

export type WebBlock = Block & {
  type: "web";
  lastScreenshot: number | null; // stored as timestamp
};

export type ImageBlock = Block & {
  type: "image";
  sticker?: boolean;
};

export type LinkBlock = Block & {
  type: "link";
};

export type ImageSearchBlock = Block & {
  type: "imageSearch";
  text: string;
  searchType: "" | "transparent" | "gif" | "gif-transparent" | "gif-text";
  offset: number;
};

export type CssBlock = Block & {
  type: "css";
};

export type ScreenBlock = Block & {
  type: "screen";
  lastScreenshot: number | null; // stored as timestamp
};

export type DrawingInputPath = [number, number][];
export type DrawingInputPaths = DrawingInputPath[];

export type SvgPath = {
  d: string;
  fill: string;
};

export type SvgData = {
  viewBox: [top: number, left: number, bottom: number, right: number];
  paths: SvgPath[];
};

export type SvgBlock = Block & {
  type: "svg";
};

export enum YoutubeBlockViewState {
  UrlInput = 0,
  PlayVideo = 1,
}

/**
 * Inspired by
 * https://developers.google.com/youtube/iframe_api_reference#getPlayerState
 */
export enum YTPlayerState {
  Ended = 0,
  Playing = 1,
  Paused = 2,
}

export type YoutubeBlock = Block & {
  type: "youtube";
  viewState: YoutubeBlockViewState;
  playerState: YTPlayerState;
  playbackTime: number;
  playbackSpeed: number;
  /** For deriving actual playbackTime with math. */
  playerUpdatedAt: number;
  /** Global hover state. */
  isBeingHovered: boolean;
};

export type Viewport = {
  position: Position; // Vector2D
  size: Size; // Vector2D
};

export type ContextMenu = {
  position: Position; // Vector2D
  hoverIndex: number;
  type: "space" | "block";
  hasNewTab?: boolean;
};

export type Preview = {
  position: Position; // Vector2D, grid unit
  size: Size; // Vector2D, grid unit
  select?: boolean;
};

export type SvgPaths = number[][][];

export type CursorType =
  | "default"
  | "text"
  | "pointer"
  | "grab"
  | "grabbing"
  | "pen"
  | "nesw-resize"
  | "nwse-resize";

export type Presence = {
  preview?: Preview | null;
  selection: Selection;
  position: Position;
  viewport: Viewport;
  contextMenu?: ContextMenu | null;
  stampMode: "inactive" | "active" | "pressed";
  cursor: CursorType;
  isInAuthFlow: boolean;
  drawMode: DrawingInputPaths | null;
};

// User
export type User = {
  id: string;
  name: string;
  color: UserColor | UserColorOld;
  anonymous?: boolean;
  playerId?: string;
  lastMessageSeenAt?: number;
};

// - a session present a visit of space by a user in a window
// - a user could initiate mutliple sessions to a same space
//   by space multiple tab
export type SessionId = string;

// Presence where key is user ID
export type UserPositions = Record<string, Point2D>;

export type UserOnlines = string[];

export type UserLocations = Record<string, UserLocation>;

export interface UserLocation {
  block?: string;
  position?: Point2D;
  size?: Size;
  locked?: boolean;
  zIndex?: number;
}

// Primitives
export type Position = Point2D;

export type Point2D = {
  x: number;
  y: number;
};

export type Size = {
  width: number;
  height: number;
};

// Utilities
export type ValueOf<T> = T[keyof T];

export type SpaceSession = {
  spaceId: string;
  spaceName?: string;
  startAt: number;
};

type UnixTime = number;

export type Selection = {
  [blockId: string]: UnixTime;
};

export type VideoStream = ILocalVideoTrack | IRemoteVideoTrack;

export type LocalConfig = {
  gallery?: boolean;
};

export type Blocks = Record<string, AnyBlock>;

// Liveblock room storage
export type RoomStorage = {
  spaceInfo?: SpaceInfo;
  blocks?: Blocks;
  blockIds?: string[];
  users?: Record<string, User>;
};
